import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { HeatTraceLoad, HeatTraceSearch } from '@completion/actions';
import { HeatTraceTests, HeatTraceTestsWeekSummary } from '@completion/models';
import { getHeatTraceLoadErrorMessage, getHeatTraceTestsLoading, getTests } from '@completion/selectors';
import { HeatTraceFilterStateService } from './heat-trace-filter-state.service';

interface IsoWeek {
  year: number;
  week: number;
}

interface HeatTraceSearchResultItem {
  test: HeatTraceTests['tests'][number];
  weekGroup: HeatTraceTests;
  displayTitle: string;
  statusName: string | null;
  handoverDate: string | null;
}

type HeatTraceStatusFilter = 'ALL' | string;
type HeatTraceSearchOrderBy = 'handoverDate' | 'name' | 'status';
type HeatTraceSearchDirection = 'DESC' | 'ASC';

@Component({
  selector: 'app-heat-trace',
  templateUrl: './heat-trace.component.html',
  styleUrls: ['./heat-trace.component.scss'],
  standalone: false,
})
export class HeatTraceComponent implements OnInit, OnDestroy {
  loadErrorMessage$ = this.store.select(getHeatTraceLoadErrorMessage);
  testsLoading$ = this.store.select(getHeatTraceTestsLoading);
  allWeekGroups: HeatTraceTests[] = [];
  selectedStartDate: Date | null = null;
  currentWeekStartDate = this.startOfIsoWeek(new Date());
  searchQuery = '';
  isSearchMode = false;
  awaitingSearchResponse = false;
  pendingOverviewReset = false;
  searchResults: HeatTraceSearchResultItem[] = [];
  selectedStatusFilter: HeatTraceStatusFilter = 'ALL';
  orderBy: HeatTraceSearchOrderBy = 'handoverDate';
  direction: HeatTraceSearchDirection = 'DESC';
  readonly orderByOptions: HeatTraceSearchOrderBy[] = ['handoverDate', 'name', 'status'];
  readonly orderByLabels: Record<HeatTraceSearchOrderBy, string> = {
    handoverDate: 'Handover date',
    name: 'Name',
    status: 'Status',
  };
  readonly directionOptions: HeatTraceSearchDirection[] = ['DESC', 'ASC'];
  availableStatusFilters: string[] = [];
  private lastSubmittedSearchQuery = '';

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly store: Store,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly heatTraceFilterState: HeatTraceFilterStateService,
  ) {}

  get canGoPrevious(): boolean {
    return true;
  }

  get canGoNext(): boolean {
    return true;
  }

  get isTodayButtonDisabled(): boolean {
    if (this.isSearchMode || this.pendingOverviewReset) {
      return false;
    }

    const currentWeekStart = this.startOfIsoWeek(new Date());
    return this.currentWeekStartDate.getTime() === currentWeekStart.getTime();
  }

  withoutTests(weekGroup: HeatTraceTests): HeatTraceTestsWeekSummary {
    const { tests, ...weekHeaderData } = weekGroup;
    return {
      ...weekHeaderData,
      count: tests.length,
      done: tests.filter((t) => t.mcStatus.name === 'OK').length,
    };
  }

  isCurrentWeek(weekGroup: HeatTraceTests): boolean {
    const todayIsoWeek = this.isoWeek(new Date());
    return weekGroup.year === todayIsoWeek.year && weekGroup.week === todayIsoWeek.week;
  }

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(
        map((queryParamMap) => {
          const weekParamRaw = queryParamMap.get('week');
          const dateParamRaw = queryParamMap.get('date');
          const searchParamRaw = queryParamMap.get('search');
          const hasSearchParam = queryParamMap.has('search');
          const statusParamRaw = queryParamMap.get('status');
          const normalizedWeek = this.normalizeWeekKey(weekParamRaw);
          const normalizedDate = dateParamRaw?.trim() || null;
          const normalizedSearch = hasSearchParam ? (searchParamRaw?.trim() ?? '') : null;
          const normalizedStatus = statusParamRaw?.trim() || null;
          return {
            week: normalizedWeek,
            date: normalizedDate,
            search: normalizedSearch,
            status: normalizedStatus,
          };
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(({ week, date, search, status }) => {
        if (search !== null) {
          this.searchQuery = search;
          this.selectedStatusFilter = status && status !== 'ALL' ? status : 'ALL';

          const shouldReloadSearch = search !== this.lastSubmittedSearchQuery || !this.isSearchMode;
          this.lastSubmittedSearchQuery = search;
          this.isSearchMode = true;

          if (shouldReloadSearch) {
            this.awaitingSearchResponse = true;
            this.store.dispatch(new HeatTraceSearch(search));
          } else {
            this.awaitingSearchResponse = false;
          }

          return;
        }

        if (date) {
          const parsedDate = new Date(date);
          if (Number.isNaN(parsedDate.getTime())) {
            return;
          }

          const formattedDate = this.formatDate(parsedDate);
          this.selectedStartDate = parsedDate;
          this.currentWeekStartDate = this.startOfIsoWeek(parsedDate);
          this.selectedStatusFilter = 'ALL';
          this.searchQuery = '';
          this.lastSubmittedSearchQuery = '';
          this.heatTraceFilterState.setFromDate(formattedDate);
          this.getHeatTraceData(undefined, formattedDate);
          return;
        }

        if (week) {
          const parsedWeek = this.parseWeekKey(week);
          if (!parsedWeek) {
            return;
          }

          this.currentWeekStartDate = this.startDateFromIsoWeek(parsedWeek.year, parsedWeek.week);
          this.selectedStartDate = null;
          this.selectedStatusFilter = 'ALL';
          this.searchQuery = '';
          this.lastSubmittedSearchQuery = '';
          this.heatTraceFilterState.setFromWeek(week);
          this.getHeatTraceData(week);
          return;
        }

        const today = new Date();
        this.selectedStartDate = null;
        this.currentWeekStartDate = this.startOfIsoWeek(today);
        this.selectedStatusFilter = 'ALL';
        this.searchQuery = '';
        this.lastSubmittedSearchQuery = '';
        this.getHeatTraceData();
      });

    this.store
      .select(getTests)
      .pipe(takeUntil(this.destroy$))
      .subscribe((weekGroups) => {
        this.allWeekGroups = [...(weekGroups ?? [])].sort(
          (left, right) => this.weekStartDate(left).getTime() - this.weekStartDate(right).getTime(),
        );
        this.searchResults = this.buildSearchResults(this.allWeekGroups);
        this.availableStatusFilters = this.buildAvailableStatusFilters(this.allWeekGroups);
        if (this.selectedStatusFilter !== 'ALL' && !this.availableStatusFilters.includes(this.selectedStatusFilter)) {
          this.selectedStatusFilter = 'ALL';
        }
      });

    this.testsLoading$.pipe(takeUntil(this.destroy$)).subscribe((loading) => {
      if (!loading) {
        if (this.awaitingSearchResponse) {
          this.awaitingSearchResponse = false;
        }

        if (this.pendingOverviewReset) {
          this.pendingOverviewReset = false;
          this.isSearchMode = false;
          this.searchQuery = '';
          this.lastSubmittedSearchQuery = '';
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getHeatTraceData(week?: string, date?: string): void {
    if (!this.pendingOverviewReset) {
      this.isSearchMode = false;
    }
    this.awaitingSearchResponse = false;
    this.store.dispatch(new HeatTraceLoad(week, date));
  }

  goToPreviousWeek(): void {
    this.navigateWithRelativeWeeks(-6);
  }

  goToNextWeek(): void {
    this.navigateWithRelativeWeeks(6);
  }

  goToCurrentWeekPage(): void {
    const today = new Date();
    const currentWeekStartDate = this.startOfIsoWeek(today);

    this.pendingOverviewReset = true;
    this.awaitingSearchResponse = false;
    this.selectedStartDate = null;
    this.currentWeekStartDate = currentWeekStartDate;
    this.heatTraceFilterState.clear();
    this.router.navigate(['/heat-trace']);
    this.store.dispatch(new HeatTraceLoad());
  }

  onStartDateChange(value: Date | string | null | undefined): void {
    if (!value) {
      this.selectedStartDate = null;
      this.navigateWithWeek(this.weekKeyFromDate(this.currentWeekStartDate));
      return;
    }

    const selectedDate = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(selectedDate.getTime())) {
      return;
    }

    this.selectedStartDate = selectedDate;
    this.currentWeekStartDate = this.startOfIsoWeek(selectedDate);
    this.navigateWithDate(this.formatDate(selectedDate));
  }

  onSearchSubmit(forceSubmit = false): void {
    const normalizedQuery = this.searchQuery.trim();
    if (!forceSubmit && normalizedQuery === this.lastSubmittedSearchQuery) {
      return;
    }

    this.lastSubmittedSearchQuery = normalizedQuery;
    this.isSearchMode = true;
    this.awaitingSearchResponse = true;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        search: normalizedQuery,
        status: this.selectedStatusFilter === 'ALL' ? null : this.selectedStatusFilter,
        date: null,
        week: null,
      },
      queryParamsHandling: 'merge',
    });
    this.store.dispatch(new HeatTraceSearch(normalizedQuery));
  }

  onOrderByChange(orderBy: HeatTraceSearchOrderBy): void {
    this.orderBy = orderBy;
  }

  getOrderByLabel(option: HeatTraceSearchOrderBy): string {
    return this.orderByLabels[option] ?? option;
  }

  onDirectionChange(direction: HeatTraceSearchDirection): void {
    this.direction = direction;
  }

  openSearchResult(tagNumber: string): void {
    this.router.navigate(['/heat-trace', tagNumber], { queryParamsHandling: 'preserve' });
  }

  goToOverviewForResult(event: MouseEvent, result: HeatTraceSearchResultItem): void {
    event.preventDefault();
    event.stopPropagation();

    // Keep search view visible behind silkscreen until overview data returns.
    this.pendingOverviewReset = true;
    this.awaitingSearchResponse = false;
    this.selectedStatusFilter = 'ALL';
    this.searchQuery = '';
    this.lastSubmittedSearchQuery = '';

    const targetWeek = this.resolveOverviewWeek(result);

    if (!targetWeek) {
      this.heatTraceFilterState.clear();
      this.store.dispatch(new HeatTraceLoad());
      this.router.navigate(['/heat-trace']);
      return;
    }

    this.heatTraceFilterState.setFromWeek(targetWeek);
    this.store.dispatch(new HeatTraceLoad(targetWeek));

    this.router.navigate(['/heat-trace'], {
      queryParams: {
        week: targetWeek,
        date: null,
        search: null,
        status: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  getSearchResultStatusColor(result: HeatTraceSearchResultItem): string {
    const rank = result.test.cpStatus?.rank ?? result.test.mcStatus?.rank;

    if (rank === 4) return 'red';
    if (rank === 3 || rank === 2) return 'orange';
    if (rank === 1) return 'green';
    return 'lightgray';
  }

  setStatusFilter(filter: HeatTraceStatusFilter): void {
    this.selectedStatusFilter = filter;

    if (this.isSearchMode) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          search: this.lastSubmittedSearchQuery,
          status: filter === 'ALL' ? null : filter,
        },
        queryParamsHandling: 'merge',
      });
    }
  }

  get filteredSearchResults(): HeatTraceSearchResultItem[] {
    const visibleResults =
      this.selectedStatusFilter === 'ALL'
        ? this.searchResults
        : this.searchResults.filter((result) => result.statusName === this.selectedStatusFilter);

    return this.sortSearchResults(visibleResults);
  }

  get areSearchSortControlsDisabled(): boolean {
    return this.isSearchMode && this.searchResults.length <= 1;
  }

  get areSearchStatusFiltersDisabled(): boolean {
    return this.isSearchMode && this.searchResults.length <= 1;
  }

  get filteredWeekGroups(): HeatTraceTests[] {
    if (this.selectedStatusFilter === 'ALL') {
      return this.allWeekGroups;
    }

    return this.allWeekGroups.map((weekGroup) => ({
      ...weekGroup,
      tests: (weekGroup.tests ?? []).filter((test) => this.getTestStatusName(test) === this.selectedStatusFilter),
    }));
  }

  private weekStartDate(weekGroup: HeatTraceTests): Date {
    const januaryFourth = new Date(Date.UTC(weekGroup.year, 0, 4));
    const januaryFourthDay = januaryFourth.getUTCDay() || 7;
    const startOfFirstIsoWeek = new Date(januaryFourth);
    startOfFirstIsoWeek.setUTCDate(januaryFourth.getUTCDate() - januaryFourthDay + 1);
    startOfFirstIsoWeek.setUTCDate(startOfFirstIsoWeek.getUTCDate() + (weekGroup.week - 1) * 7);

    return new Date(startOfFirstIsoWeek.getUTCFullYear(), startOfFirstIsoWeek.getUTCMonth(), startOfFirstIsoWeek.getUTCDate());
  }

  private buildSearchResults(weekGroups: HeatTraceTests[]): HeatTraceSearchResultItem[] {
    const items: HeatTraceSearchResultItem[] = [];

    for (const weekGroup of weekGroups) {
      for (const test of weekGroup.tests ?? []) {
        items.push({
          test,
          weekGroup,
          displayTitle: (test.name && test.name.trim()) || test.tagNumber,
          statusName: this.getTestStatusName(test),
          handoverDate: this.resolveHandoverDate(test, weekGroup),
        });
      }
    }

    return items;
  }

  private sortSearchResults(results: HeatTraceSearchResultItem[]): HeatTraceSearchResultItem[] {
    const directionFactor = this.direction === 'ASC' ? 1 : -1;

    return [...results].sort((left, right) => {
      let compareValue: number;

      switch (this.orderBy) {
        case 'name':
          compareValue = left.displayTitle.localeCompare(right.displayTitle);
          break;
        case 'status':
          compareValue = (left.statusName ?? '').localeCompare(right.statusName ?? '');
          break;
        case 'handoverDate':
        default:
          compareValue = this.handoverDateSortValue(left.handoverDate) - this.handoverDateSortValue(right.handoverDate);
          break;
      }

      if (compareValue !== 0) {
        return compareValue * directionFactor;
      }

      return left.test.tagNumber.localeCompare(right.test.tagNumber);
    });
  }

  private getTestStatusName(test: HeatTraceTests['tests'][number]): string | null {
    return test.mcStatus?.name ?? test.cpStatus?.name ?? null;
  }

  private buildAvailableStatusFilters(weekGroups: HeatTraceTests[]): string[] {
    const statusSet = new Set<string>();

    for (const weekGroup of weekGroups) {
      for (const test of weekGroup.tests ?? []) {
        const statusName = this.getTestStatusName(test);
        if (statusName) {
          statusSet.add(statusName);
        }
      }
    }

    return Array.from(statusSet).sort((left, right) => left.localeCompare(right));
  }

  private resolveHandoverDate(test: HeatTraceTests['tests'][number], weekGroup: HeatTraceTests): string | null {
    const rawDate = test.handoverDate?.trim();
    if (rawDate) {
      const parsedWeek = this.parseWeekKey(rawDate);
      if (parsedWeek) {
        return `${parsedWeek.year}-${`${parsedWeek.week}`.padStart(2, '0')}`;
      }

      const parsedDate = new Date(rawDate);
      if (!Number.isNaN(parsedDate.getTime())) {
        return this.formatDate(parsedDate);
      }

      return rawDate;
    }

    if (weekGroup.week >= 1 && weekGroup.week <= 53) {
      return `${weekGroup.year}-${`${weekGroup.week}`.padStart(2, '0')}`;
    }

    return null;
  }

  private handoverDateSortValue(value: string | null): number {
    if (!value) {
      return 0;
    }

    const trimmedValue = value.trim();
    const parsedWeek = this.parseWeekKey(trimmedValue);
    if (parsedWeek) {
      return this.startDateFromIsoWeek(parsedWeek.year, parsedWeek.week).getTime();
    }

    const parsedDate = new Date(trimmedValue);
    return Number.isNaN(parsedDate.getTime()) ? 0 : parsedDate.getTime();
  }

  private resolveOverviewWeek(result: HeatTraceSearchResultItem): string | null {
    const handoverDate = result.test.handoverDate?.trim();
    if (handoverDate) {
      const parsedWeek = this.parseWeekKey(handoverDate);
      if (parsedWeek) {
        return `${parsedWeek.year}-${`${parsedWeek.week}`.padStart(2, '0')}`;
      }

      const parsedDate = new Date(handoverDate);
      if (!Number.isNaN(parsedDate.getTime())) {
        return this.weekKeyFromDate(parsedDate);
      }
    }

    return null;
  }

  private startOfIsoWeek(date: Date): Date {
    const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dayOfWeek = normalizedDate.getDay() || 7;
    normalizedDate.setDate(normalizedDate.getDate() - dayOfWeek + 1);
    return normalizedDate;
  }

  private navigateWithRelativeWeeks(weekOffset: number): void {
    const targetWeekStartDate = new Date(this.currentWeekStartDate);
    targetWeekStartDate.setDate(targetWeekStartDate.getDate() + weekOffset * 7);
    this.currentWeekStartDate = targetWeekStartDate;
    this.selectedStartDate = null;
    this.navigateWithWeek(this.weekKeyFromDate(targetWeekStartDate));
  }

  private navigateWithWeek(week: string): void {
    this.heatTraceFilterState.setFromWeek(week);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        week,
        date: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  private navigateWithDate(date: string): void {
    this.heatTraceFilterState.setFromDate(date);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        date,
        week: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  private isoWeek(date: Date): IsoWeek {
    const normalizedDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNumber = normalizedDate.getUTCDay() || 7;
    normalizedDate.setUTCDate(normalizedDate.getUTCDate() + 4 - dayNumber);
    const yearStart = new Date(Date.UTC(normalizedDate.getUTCFullYear(), 0, 1));
    const week = Math.ceil(((normalizedDate.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
    return {
      year: normalizedDate.getUTCFullYear(),
      week,
    };
  }

  private startDateFromIsoWeek(year: number, week: number): Date {
    const januaryFourth = new Date(Date.UTC(year, 0, 4));
    const januaryFourthDay = januaryFourth.getUTCDay() || 7;
    const startOfFirstIsoWeek = new Date(januaryFourth);
    startOfFirstIsoWeek.setUTCDate(januaryFourth.getUTCDate() - januaryFourthDay + 1);
    startOfFirstIsoWeek.setUTCDate(startOfFirstIsoWeek.getUTCDate() + (week - 1) * 7);
    return new Date(startOfFirstIsoWeek.getUTCFullYear(), startOfFirstIsoWeek.getUTCMonth(), startOfFirstIsoWeek.getUTCDate());
  }

  private weekKeyFromDate(date: Date): string {
    const isoWeek = this.isoWeek(date);
    return `${isoWeek.year}-${`${isoWeek.week}`.padStart(2, '0')}`;
  }

  private parseWeekKey(weekKey: string): IsoWeek | null {
    const match = weekKey.match(/^(\d{4})-(\d{2})$/);
    if (!match) {
      return null;
    }

    const year = Number(match[1]);
    const week = Number(match[2]);
    if (!Number.isInteger(year) || !Number.isInteger(week) || week < 1 || week > 53) {
      return null;
    }

    return { year, week };
  }

  private normalizeWeekKey(rawWeek: string | null): string | null {
    if (!rawWeek) {
      return null;
    }

    const parsedWeek = this.parseWeekKey(rawWeek.trim());
    if (!parsedWeek) {
      return null;
    }

    return `${parsedWeek.year}-${`${parsedWeek.week}`.padStart(2, '0')}`;
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
